import KeenSlider from 'keen-slider';

if (!customElements.get('ia-slider')) {

  class IASlider extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.currentSlideElement = null;
      this.prevButton = null;
      this.nextButton = null;
      this.progressFill = null;
      this.loop = this.dataset.loop === 'true';
      this.wheelScroll = this.dataset.wheelScroll === 'true';
    }
  
    connectedCallback() {
      // Defer initialization if data-defer-init is set
      if (this.dataset.deferInit === 'true') {
        return;
      }
      this.initializeSlideshow();
    }
  
    disconnectedCallback() {
      if (this.slider) {
        this.slider.destroy();
      }
    }
  
    initializeSlideshow() {
      const carousel = this.querySelector('.ia-slider__slides');
      this.currentSlideElement = this.querySelector('.js-ia-slider-current');
      this.prevButton = this.querySelector('.js-ia-slider-prev');
      this.nextButton = this.querySelector('.js-ia-slider-next');
      this.progressFill = this.querySelector('.ia-slider__progress-fill');
  
      if (!carousel) return;

      // Wheel scroll + free mode only on desktop (mobile stays default snap, no wheel)
      const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
      const wheelScrollDesktop = this.wheelScroll && isDesktop;

      const plugins = wheelScrollDesktop ? [this.createHorizontalWheelPlugin()] : [];
  
      this.slider = new KeenSlider(carousel, {
        loop: this.loop,
        ...(wheelScrollDesktop && { mode: 'free' }),
        slides: {
          perView: this.style.getPropertyValue('--items-desktop'),
          spacing: this.style.getPropertyValue('--spacing-desktop').replace('px', ''),
        },
        breakpoints: {
          '(max-width: 1023px)': {
            slides: {
              perView: this.style.getPropertyValue('--items-mobile'),
              spacing: this.style.getPropertyValue('--spacing-mobile').replace('px', ''),
              origin: this.dataset.centeredMobile === 'true' ? 0.25 : undefined,
            },
          },
          '(min-width: 1600px)': {
            slides: {
              spacing: this.style.getPropertyValue('--spacing-desktop').replace('px', ''),
              perView: this.style.getPropertyValue('--items-desktop-large') || this.style.getPropertyValue('--items-desktop'),
            },
          },
        },
        created: (slider) => {
          this.classList.add('is-initialized');
          const firstSlide = this.querySelector('.ia-slider__slide');
          if (firstSlide) {
            firstSlide.classList.add('is-active');
          }
          this.createProgressBar();

          this.syncAriaHidden(slider);
          
          // Update progress after slider is created
          setTimeout(() => {
            this.updateProgress();
          }, 50);
        },
        slideChanged: (slider) => {
          this.updateProgress();
        },
        animationEnded: (slider) => {
          // slideChanged would trigger too fast
          this.syncAriaHidden(slider);
        },
      }, plugins)
  
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => {
          this.slider.prev();
        });
      }
  
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => {
          this.slider.next();
        });
      }
  
      this.slider.on('slideChanged', (slide) => {
        const allSlides = this.querySelectorAll('.ia-slider__slide');
        allSlides.forEach(slide => slide.classList.remove('is-active'));
        
        const currentSlide = this.slider.track.details.rel;
        if (allSlides[currentSlide]) {
          allSlides[currentSlide].classList.add('is-active');
        }
        
        if (this.slider?.track?.details?.rel >= 0 && this.currentSlideElement) {
          this.currentSlideElement.textContent = this.slider.track.details.rel + 1;
        }
        
        this.updateProgress();
      });

    }
  
    createProgressBar() {
      if (this.dataset.showProgress !== 'true') return;

      if (this.querySelector('.ia-slider__progress')) return;

      const progressContainer = document.createElement('div');
      progressContainer.className = 'ia-slider__progress ' + this.dataset.colorSchemeUi;

      const progressBar = document.createElement('div');
      progressBar.className = 'ia-slider__progress-bar';

      const progressFill = document.createElement('div');
      progressFill.className = 'ia-slider__progress-fill';

      progressBar.appendChild(progressFill);
      progressContainer.appendChild(progressBar);
      this.appendChild(progressContainer);

      this.progressFill = progressFill;
    }

    updateProgress() {
      const d = this.slider?.track?.details;
      const totalSlides = d?.slides?.length || 0;
      const currentSlide = d?.rel;
      const minIdx = typeof d?.minIdx === 'number' ? d?.minIdx : 0;
      const maxIdx = typeof d?.maxIdx === 'number' ? d?.maxIdx : (totalSlides - 1);
      const steps = Math.max(1, (maxIdx - minIdx + 1));
      const atStart = currentSlide <= minIdx;
      const atEnd   = currentSlide >= maxIdx;
      if (this.dataset.showProgress === 'true' && this.progressFill && this.slider) {
        
        // Only update if we have valid data
        if (totalSlides > 0) {
          // const progress = ((currentSlide + 1) / totalSlides) * 100;
          // this.progressFill.style.width = `${Math.min(progress, 100)}%`;
          
          // Progress by "page" (works with perView > 1)
          const page = currentSlide - minIdx + 1;
          const progress = Math.min(100, (page / steps) * 100);
          this.progressFill.style.width = `${progress}%`;
        }
      }

      if (this.prevButton) {
        this.prevButton.classList.toggle('ia-visibility-hidden', atStart);
      }

      if (this.nextButton) {
        this.nextButton.classList.toggle('ia-visibility-hidden', atEnd);
      }
    }

    syncAriaHidden(slider) {
      const d = slider?.track?.details;
      if (!d?.slides?.length) return;
    
      slider.slides.forEach((el, i) => {
        const portion = d.slides[i]?.portion ?? 0; // 0..1 = how much of slide is visible
        const isVisible = portion > 0.001;         // tiny threshold to avoid float noise
    
        el.setAttribute("aria-hidden", isVisible ? "false" : "true");
      });
    }

    /**
     * Plugin for KeenSlider: horizontal wheel/touchpad scroll only.
     * Smooth follow: accumulate deltas into a target and interpolate toward it on rAF.
     * Vertical wheel is ignored so the page can scroll normally.
     */
    createHorizontalWheelPlugin() {
      const SMOOTH_FACTOR = 0.22; // lerp per frame (~60fps) – higher = snappier
      const IDLE_FRAMES = 4;     // stop rAF after this many frames near target

      return function HorizontalWheelPlugin(slider) {
        let targetPosition = null;
        let rafId = null;
        let idleCount = 0;

        function eventWheel(e) {
          const isHorizontal = Math.abs(e.deltaX) >= Math.abs(e.deltaY);
          if (!isHorizontal) return;

          e.preventDefault();
          const d = slider.track.details;
          const size = slider.size;
          if (!d || !size) return;

          const delta = e.deltaX / size;
          if (targetPosition === null) targetPosition = d.position;
          targetPosition = Math.min(d.max, Math.max(d.min, targetPosition + delta));
          idleCount = 0;
          if (rafId === null) rafId = requestAnimationFrame(tick);
        }

        function tick() {
          rafId = null;
          const d = slider.track.details;
          if (!d) return;
          if (targetPosition === null) targetPosition = d.position;
          const current = d.position;
          const diff = targetPosition - current;
          const threshold = 0.001;
          if (Math.abs(diff) < threshold) {
            idleCount++;
            if (idleCount >= IDLE_FRAMES) {
              targetPosition = null;
              return;
            }
          } else {
            idleCount = 0;
            const next = current + diff * SMOOTH_FACTOR;
            const clamped = Math.min(d.max, Math.max(d.min, next));
            slider.track.to(clamped);
          }
          rafId = requestAnimationFrame(tick);
        }

        slider.on('created', () => {
          slider.container.addEventListener('wheel', eventWheel, { passive: false });
        });

        slider.on('destroyed', () => {
          if (rafId !== null) cancelAnimationFrame(rafId);
        });
      };
    }
  
    next() {
      if (this.slider) {
        this.slider.next();
      }
    }
  
    prev() {
      if (this.slider) {
        this.slider.prev();
      }
    }
  
    goToSlide(index) {
      if (this.slider) {
        this.slider.moveToIdx(index);
      }
    }
  
    destroy() {
      if (this.slider) {
        this.slider.destroy();
        this.slider = null;
      }
    }
  }
  
  
  customElements.define('ia-slider', IASlider); 
}